<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  variables: {
    type: Array,
    required: true,
  },
  macroName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();

// Create a ref object with all variables initialized to empty strings
const variableValues = ref({});

// Initialize variable values when variables change
watch(
  () => props.variables,
  newVariables => {
    if (newVariables && newVariables.length > 0) {
      const values = {};
      newVariables.forEach(varName => {
        values[varName] = variableValues.value[varName] || '';
      });
      variableValues.value = values;
    }
  },
  { immediate: true }
);

const isFormValid = computed(() => {
  // Check if all variables have values
  return props.variables.every(varName => {
    const value = variableValues.value[varName];
    return value && value.trim().length > 0;
  });
});

const handleSubmit = () => {
  if (isFormValid.value) {
    emit('submit', variableValues.value);
  }
};

const handleClose = () => {
  emit('close');
};

// Format variable name for display (remove underscores, capitalize)
const formatVariableName = varName => {
  return varName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};
</script>

<template>
  <div v-if="show">
    <!-- Header Section -->
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ macroName }}
        </h3>
      </div>
      <div class="text-xs text-n-slate-11">
        {{ t('MACROS.VARIABLE_MODAL.SUBTITLE', { macroName }) }}
      </div>
    </div>

    <!-- Variables Section -->
    <div>
      <p class="mb-2.5 text-sm font-semibold">
        {{ t('MACROS.VARIABLE_MODAL.TITLE') }}
      </p>
      <div
        v-for="variable in variables"
        :key="variable"
        class="flex items-center mb-2.5"
      >
        <Input
          v-model="variableValues[variable]"
          type="text"
          class="flex-1"
          :placeholder="
            t('MACROS.VARIABLE_MODAL.INPUT_PLACEHOLDER', {
              name: formatVariableName(variable),
            })
          "
        />
      </div>

      <p
        v-if="!isFormValid && Object.keys(variableValues).length > 0"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9 text-sm"
      >
        {{ t('MACROS.VARIABLE_MODAL.VALIDATION_ERROR') }}
      </p>
    </div>

    <!-- Action Buttons -->
    <div class="flex gap-2 mt-4">
      <NextButton slate class="flex-1" @click="handleClose">
        {{ t('MACROS.VARIABLE_MODAL.CANCEL') }}
      </NextButton>
      <NextButton
        primary
        class="flex-1"
        :disabled="!isFormValid"
        @click="handleSubmit"
      >
        {{ t('MACROS.VARIABLE_MODAL.SUBMIT') }}
      </NextButton>
    </div>
  </div>
</template>
